import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { App as AppNativo } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import type { EstadoVersionApp, TipoAcceso, Permiso } from '@cmms/shared';
import { tienePermiso } from '@cmms/shared';
import { api, ErrorApi, registrarSesionExpirada } from './api.js';
import { notificarErrorGlobal } from '../componentes/notificaciones-globales.js';

/** Cada cuánto se repite el chequeo mientras la app sigue abierta en primer plano. */
const INTERVALO_VERIFICACION_VERSION_MS = 5 * 60_000;

export interface UsuarioSesion {
  id: string;
  nombreCompleto: string;
  email: string | null;
  tipoAcceso: TipoAcceso;
  /** Permisos que añade el grupo (hoy: listas de materiales), además de los del tipo de acceso. */
  permisosExtra?: string[];
  tenantSlug: string;
  razonSocial: string;
  /**
   * Doble aceptación (continnua_plataforma/docs/FASE-2.md §5): «qué modo de
   * acceso está activo» lo expone `GET /auth/yo` (`ContextoPeticion.modoAcceso`
   * en el backend). Ausente —no `'legado'`— en el login propio del módulo: se
   * declara opcional y se consume de forma tolerante a que falte.
   */
  modoAcceso?: 'nucleo';
  /**
   * URL del portal Continnua donde esta organización gestiona a su gente, o ausente/`null` si la
   * sigue gestionando aquí (`continnua_plataforma/docs/adr/ADR-007`). Lo decide el backend por
   * tenant —no por entorno—, porque durante el piloto conviven tenants migrados y sin migrar bajo
   * el mismo `NUCLEO_HABILITADO`.
   *
   * Opcional en el tipo por la misma razón que `modoAcceso`: una web desplegada contra un API
   * anterior no debe romperse, solo seguir ofreciendo el alta local.
   */
  gestionUsuariosEnNucleo?: string | null;
  /** Cuenta compartida por varias personas (§ pedido), p. ej. la cuenta de una sede. */
  esCompartido?: boolean;
}

/** Una entrada del selector de empresas (`GET /auth/nucleo/organizaciones`). */
export interface OrganizacionDisponible {
  id: string;
  slug: string;
  nombre: string;
  esActual: boolean;
}

interface Sesion {
  usuario: UsuarioSesion | null;
  cargando: boolean;
  /**
   * `true` mientras se resuelve si la versión nativa instalada es válida, antes de saber siquiera
   * si hay sesión. Solo aplica dentro de Capacitor: en el navegador de escritorio no hay "versión
   * instalada" que revisar, así que ahí queda en `false` desde el principio.
   */
  verificandoVersion: boolean;
  /** Bloquea TODA la app (incluido el login), sin importar la versión instalada: mantenimiento del backend. */
  mantenimiento: boolean;
  mensajeMantenimiento: string | null;
  /**
   * Bloquea TODA la app (incluido el login). Por debajo de la versión mínima
   * (`versionMinima`, el piso de compatibilidad) SIEMPRE es forzada; entre el piso y
   * `versionActual` lo decide el operador (`forzarActualizacion` en `platform.versiones_app`).
   */
  actualizacionForzada: boolean;
  /**
   * Hay una versión más nueva, pero no es obligatoria: un aviso descartable, no un bloqueo.
   * Se vuelve a mostrar si sale otra versión distinta a la que ya se descartó.
   */
  actualizacionOpcional: boolean;
  descartarActualizacionOpcional: () => void;
  /** Repite la verificación a demanda — el botón "Reintentar" de la pantalla de mantenimiento. */
  reverificarVersion: () => Promise<void>;
  urlTienda: string | null;
  urlDescarga: string | null;
  /** Si el núcleo Continnua está habilitado para este entorno (`GET /auth/nucleo/estado`). */
  nucleoHabilitado: boolean;
  /**
   * Empresas del usuario actual para el selector del header. `null` mientras no se ha pedido
   * (o el núcleo está apagado, o el usuario entró por el formulario local): pedirla de más
   * gastaría un refresh+rotación del token del núcleo en cada carga de página sin necesidad.
   */
  organizaciones: OrganizacionDisponible[] | null;
  cargandoOrganizaciones: boolean;
  cargarOrganizaciones: () => Promise<void>;
  iniciar: (email: string, contrasena: string, tenant: string) => Promise<void>;
  /** Redirige el navegador al inicio del flujo `code + PKCE` que conduce el API como BFF. */
  iniciarConNucleo: () => void;
  cambiarOrganizacion: (orgId: string) => Promise<void>;
  salir: () => Promise<void>;
  puede: (permiso: Permiso) => boolean;
}

interface RespuestaOrganizaciones {
  orgs: Array<{
    organization: { id: string; slug: string; displayName: string };
    isDefault: boolean;
  }>;
}

const ContextoSesion = createContext<Sesion | null>(null);

export function ProveedorSesion({ children }: { children: ReactNode }): JSX.Element {
  const [usuario, setUsuario] = useState<UsuarioSesion | null>(null);
  const [cargando, setCargando] = useState(true);
  const [verificandoVersion, setVerificandoVersion] = useState(Capacitor.isNativePlatform());
  const [estadoVersion, setEstadoVersion] = useState<EstadoVersionApp | null>(null);
  const [nucleoHabilitado, setNucleoHabilitado] = useState(false);
  const [organizaciones, setOrganizaciones] = useState<OrganizacionDisponible[] | null>(null);
  const [cargandoOrganizaciones, setCargandoOrganizaciones] = useState(false);

  // `usuarioRef` es la fuente de verdad para el oyente de abajo: si solo
  // mirara el `usuario` del closure, un 401 durante la restauración inicial
  // (todavía sin sesión) dispararía el mismo aviso que uno real a mitad de
  // trabajo, y el usuario vería «tu sesión expiró» sin haber iniciado sesión.
  const usuarioRef = useRef(usuario);
  usuarioRef.current = usuario;

  useEffect(() => {
    registrarSesionExpirada('tenant', () => {
      if (usuarioRef.current === null) return;
      usuarioRef.current = null;
      setUsuario(null);
      notificarErrorGlobal('Tu sesión expiró. Vuelve a iniciar sesión.');
    });
    return () => registrarSesionExpirada('tenant', null);
  }, []);

  // `null` = nunca se descartó nada; una vez que se descarta, guarda la `versionActual` que
  // tenía el aviso en ese momento. Si luego sale otra versión distinta, ya no coincide y el
  // aviso opcional vuelve a aparecer — descartar no calla avisos futuros para siempre.
  const [versionOpcionalDescartada, setVersionOpcionalDescartada] = useState<string | null>(null);

  // Al cargar, se intenta restaurar la sesión desde la cookie. Si el token de
  // acceso caducó, el cliente lo renueva solo antes de rendirse. En paralelo se
  // pregunta si el acceso con Continnua está habilitado: la página de acceso lo
  // necesita para decidir si ofrece el botón, y no puede leerlo de `/auth/yo`
  // porque esa ruta exige sesión y, en la página de acceso, no hay ninguna.
  // Se resuelve antes que nada, incluso antes de intentar restaurar la sesión: una versión
  // desactualizada bloquea también el login, así que no tiene sentido pedir `/auth/yo` primero.
  // Solo corre dentro de Capacitor — en el navegador de escritorio no hay binario que actualizar.
  //
  // Además de al arrancar, se repite:
  //  · cada vez que la app vuelve a primer plano (`appStateChange` con `isActive: true`):
  //    alguien puede dejarla en segundo plano, actualizarla desde la tienda o desde otro
  //    dispositivo enterarse de que subió la versión mínima, y sin este disparo el bloqueo
  //    tardaría hasta el siguiente cierre completo del proceso en aparecer.
  //  · cada `INTERVALO_VERIFICACION_VERSION_MS` mientras sigue en primer plano: una sesión
  //    larga sin minimizar nunca (un técnico con la app abierta toda la jornada) no dispararía
  //    ningún otro evento. El intervalo se pausa en segundo plano para no gastar batería/datos
  //    verificando una pantalla que nadie está mirando.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let vigente = true;
    let verificando = false;
    let intervalo: ReturnType<typeof setInterval> | undefined;

    const verificarVersion = async (): Promise<void> => {
      if (verificando) return;
      verificando = true;
      try {
        const plataforma = Capacitor.getPlatform();
        const info = await AppNativo.getInfo();
        const respuesta = await api.get<EstadoVersionApp>(
          `/auth/app-version?plataforma=${encodeURIComponent(plataforma)}&version=${encodeURIComponent(info.version)}`,
        );
        if (!vigente) return;
        setEstadoVersion(respuesta);
      } catch {
        // Sin poder verificar (API caída, endpoint viejo) se deja pasar: negar el acceso por un
        // fallo de red propio sería peor que dejar entrar a alguien con la versión mínima sin fijar.
        // Se deja el estado anterior tal cual: si ya se sabía bloqueada por una verificación previa,
        // un fallo de red aislado no la desbloquea.
      } finally {
        verificando = false;
        if (vigente) setVerificandoVersion(false);
      }
    };

    void verificarVersion();

    const suscripcion = AppNativo.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        void verificarVersion();
        intervalo ??= setInterval(() => void verificarVersion(), INTERVALO_VERIFICACION_VERSION_MS);
      } else if (intervalo) {
        clearInterval(intervalo);
        intervalo = undefined;
      }
    });
    intervalo = setInterval(() => void verificarVersion(), INTERVALO_VERIFICACION_VERSION_MS);

    return () => {
      vigente = false;
      if (intervalo) clearInterval(intervalo);
      void suscripcion.then((s) => s.remove());
    };
  }, []);

  useEffect(() => {
    let vigente = true;

    void (async () => {
      try {
        const perfil = await api.get<UsuarioSesion>('/auth/yo');
        if (vigente) setUsuario(perfil);
      } catch {
        if (vigente) setUsuario(null);
      } finally {
        if (vigente) setCargando(false);
      }
    })();

    void (async () => {
      try {
        const estado = await api.get<{ habilitado: boolean }>('/auth/nucleo/estado');
        if (vigente) setNucleoHabilitado(estado.habilitado);
      } catch {
        // Sin la ruta (núcleo apagado, o el módulo ni siquiera está montado) el
        // acceso se comporta exactamente como antes: solo el formulario propio.
        if (vigente) setNucleoHabilitado(false);
      }
    })();

    return () => {
      vigente = false;
    };
  }, []);

  const iniciar = useCallback(async (email: string, contrasena: string, tenant: string) => {
    const respuesta = await api.post<{ usuario: UsuarioSesion }>('/auth/sesion', {
      email,
      contrasena,
      tenant,
    });
    setUsuario(respuesta.usuario);
  }, []);

  const iniciarConNucleo = useCallback(() => {
    // Navegación de página completa, no una llamada del cliente: es el propio
    // navegador el que debe seguir la redirección a `/oauth/authorize` del
    // núcleo y, más tarde, la vuelta a `/auth/nucleo/retorno`. Un `fetch` no
    // sirve aquí — perdería las cookies de tránsito de esa navegación.
    window.location.assign('/api/auth/nucleo/inicio');
  }, []);

  const cargarOrganizaciones = useCallback(async () => {
    if (!nucleoHabilitado || usuarioRef.current?.modoAcceso !== 'nucleo') return;
    setCargandoOrganizaciones(true);
    try {
      const respuesta = await api.get<RespuestaOrganizaciones>('/auth/nucleo/organizaciones');
      // `isDefault` es la organización predeterminada del usuario, no la activa en esta sesión
      // (eso lo dice `tenantSlug`, que viene de los claims de la sesión actual, no de la lista):
      // confundirlas dejaría el selector marcando como "actual" la que no lo es tras un cambio.
      const tenantActivo = usuarioRef.current?.tenantSlug;
      setOrganizaciones(
        respuesta.orgs.map((entrada) => ({
          id: entrada.organization.id,
          slug: entrada.organization.slug,
          nombre: entrada.organization.displayName,
          esActual: entrada.organization.slug === tenantActivo,
        })),
      );
    } catch (error) {
      if (!(error instanceof ErrorApi)) throw error;
      // Sin lista no hay selector, pero no debe romper el resto de la página.
    } finally {
      setCargandoOrganizaciones(false);
    }
  }, [nucleoHabilitado]);

  const descartarActualizacionOpcional = useCallback(() => {
    setVersionOpcionalDescartada(estadoVersion?.actual ?? null);
  }, [estadoVersion]);

  const reverificarVersion = useCallback(async (): Promise<void> => {
    if (!Capacitor.isNativePlatform()) return;
    try {
      const plataforma = Capacitor.getPlatform();
      const info = await AppNativo.getInfo();
      const respuesta = await api.get<EstadoVersionApp>(
        `/auth/app-version?plataforma=${encodeURIComponent(plataforma)}&version=${encodeURIComponent(info.version)}`,
      );
      setEstadoVersion(respuesta);
    } catch {
      // Igual que la verificación automática: sin respuesta, se deja el estado anterior.
    }
  }, []);

  const cambiarOrganizacion = useCallback(async (orgId: string) => {
    const nuevoUsuario = await api.post<UsuarioSesion>('/auth/nucleo/cambiar-organizacion', { orgId });
    setUsuario(nuevoUsuario);
    // La lista queda vieja tras el cambio (otro `isDefault`, quizá otras orgs visibles); se
    // vuelve a pedir la próxima vez que se abra el selector en vez de arrastrarla.
    setOrganizaciones(null);
  }, []);

  const salir = useCallback(async () => {
    if (nucleoHabilitado) {
      // Navegación de página completa, no un `fetch`: `GET /api/auth/nucleo/salir`
      // termina redirigiendo al núcleo para que borre su propia cookie de sesión
      // SSO, y eso solo lo hace el navegador cuando la petición es una navegación
      // de nivel superior. Un `fetch` (con o sin `credentials`) deja viva esa
      // sesión y el siguiente «Entrar con Continnua» vuelve a entrar sin pedir
      // credenciales — la ruta también limpia la sesión local cuando no hay
      // ninguna sesión del núcleo que cerrar, así que es segura de usar siempre
      // que el interruptor esté encendido.
      window.location.assign('/api/auth/nucleo/salir');
      return;
    }

    await api.post('/auth/salir').catch((error: unknown) => {
      // Cerrar sesión no debe fallar de cara al usuario: si el servidor no
      // responde, se limpia igual en el cliente.
      if (!(error instanceof ErrorApi)) throw error;
    });
    setUsuario(null);
  }, [nucleoHabilitado]);

  // El piso mínimo bloquea siempre; el salto hasta la última versión bloquea solo si el
  // operador marcó `forzarActualizacion` — el backend ya hizo esa cuenta (`forzada`), aquí
  // solo se separa en dos banderas para que la UI decida bloqueo total vs. aviso descartable.
  const actualizacionForzada = estadoVersion?.actualizacionRequerida === true && estadoVersion.forzada;
  const actualizacionOpcional =
    estadoVersion?.actualizacionRequerida === true &&
    !estadoVersion.forzada &&
    estadoVersion.actual !== versionOpcionalDescartada;

  const valor = useMemo<Sesion>(
    () => ({
      usuario,
      cargando,
      verificandoVersion,
      mantenimiento: estadoVersion?.mantenimiento ?? false,
      mensajeMantenimiento: estadoVersion?.mensajeMantenimiento ?? null,
      actualizacionForzada,
      actualizacionOpcional,
      descartarActualizacionOpcional,
      reverificarVersion,
      urlTienda: estadoVersion?.urlTienda ?? null,
      urlDescarga: estadoVersion?.urlDescarga ?? null,
      nucleoHabilitado,
      organizaciones,
      cargandoOrganizaciones,
      cargarOrganizaciones,
      iniciar,
      iniciarConNucleo,
      cambiarOrganizacion,
      salir,
      // El permiso se consulta también en el cliente para no ofrecer acciones
      // que el servidor va a rechazar. La decisión real sigue siendo del guard.
      puede: (permiso) =>
        usuario ? tienePermiso(usuario.tipoAcceso, permiso) || (usuario.permisosExtra ?? []).includes(permiso) : false,
    }),
    [
      usuario,
      cargando,
      verificandoVersion,
      estadoVersion,
      actualizacionForzada,
      actualizacionOpcional,
      descartarActualizacionOpcional,
      reverificarVersion,
      nucleoHabilitado,
      organizaciones,
      cargandoOrganizaciones,
      cargarOrganizaciones,
      iniciar,
      iniciarConNucleo,
      cambiarOrganizacion,
      salir,
    ],
  );

  return <ContextoSesion.Provider value={valor}>{children}</ContextoSesion.Provider>;
}

export function useSesion(): Sesion {
  const contexto = useContext(ContextoSesion);
  if (!contexto) throw new Error('useSesion debe usarse dentro de ProveedorSesion');
  return contexto;
}
